import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  Loader2, Building2, Users, CreditCard, Megaphone, ClipboardList,
  ListChecks, Headset, ShieldAlert, Flag, MailWarning, ShieldCheck, MessageSquare,
} from "lucide-react";

const TILES = [
  // First row: high-level platform counts.
  { key: "users",        label: "Users",          icon: Users,        valueFrom: (d) => d.users.total,
    sub: (d) => `${d.users.business_owners} owners · ${d.users.customers} customers · ${d.users.promoters} promoters`,
    to:  "/admin/users" },
  { key: "companies",    label: "Businesses",     icon: Building2,    valueFrom: (d) => d.companies.total,
    sub: (d) => `${d.companies.active} active · ${d.companies.suspended} suspended`,
    to:  "/admin/businesses" },
  { key: "bookings",     label: "Total Bookings", icon: CreditCard,   valueFrom: (d) => d.bookings_total,
    sub: () => "All-time" },
  { key: "inquiries",    label: "Total Inquiries", icon: MessageSquare, valueFrom: (d) => d.inquiries_total ?? 0,
    sub: () => "All-time" },

  // Second row: operational triage.
  { key: "open_tickets", label: "Open support tickets", icon: Headset, valueFrom: (d) => d.open_support_tickets ?? 0,
    sub: (d) => `${d.abuse_reports_open ?? 0} flagged as abuse`,
    to:  "/admin/support" },
  { key: "review_reports", label: "Pending review reports", icon: Flag, valueFrom: (d) => d.pending_review_reports ?? 0,
    sub: () => "Awaiting moderation",
    to:  "/admin/reports" },
  { key: "promoters",    label: "Active promoters", icon: Megaphone, valueFrom: (d) => d.promoters_active,
    sub: () => "Registered & active",
    to:  "/admin/promoters" },
  { key: "tasks",        label: "Open promoter tasks", icon: ListChecks, valueFrom: (d) => d.open_tasks,
    sub: () => "Assigned or in progress",
    to:  "/admin/promoter-tasks" },

  // Third row: compliance.
  { key: "stale_legal",  label: "Missing latest legal", icon: ShieldAlert, valueFrom: (d) => d.users_missing_latest_legal ?? 0,
    sub: (d) => `Current bundle: ${d.current_legal_bundle_version}`,
    to:  "/admin/compliance?tab=users" },
  { key: "biz_terms",    label: "Missing business terms", icon: ShieldCheck, valueFrom: (d) => d.businesses_missing_business_terms ?? 0,
    sub: () => "Businesses pending acceptance",
    to:  "/admin/compliance?tab=businesses" },
  { key: "unverified",   label: "Unverified emails", icon: MailWarning, valueFrom: (d) => d.unverified_emails ?? 0,
    sub: () => "Filter in Users / Compliance",
    to:  "/admin/compliance?tab=users&unverified=1" },
  { key: "outreach",     label: "Outreach logs",  icon: ClipboardList, valueFrom: (d) => d.outreach_logs_total,
    sub: () => "All-time submissions",
    to:  "/admin/outreach-logs" },
];

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/overview")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);
  if (err) return <div className="text-rose-600 text-sm">{String(err)}</div>;
  if (!data) return (
    <div className="flex items-center gap-2 text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
  return (
    <div data-testid="admin-overview-page">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold">Super Admin Overview</h1>
      <p className="text-sm text-slate-600 mt-1">
        Snapshot of the entire ConfirmPH platform: operations, compliance and growth in one place.
      </p>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {TILES.map((t) => (
          <Tile
            key={t.key}
            testId={`tile-${t.key}`}
            icon={t.icon}
            title={t.label}
            value={t.valueFrom(data)}
            sub={t.sub(data)}
            to={t.to}
          />
        ))}
      </div>
    </div>
  );
}

function Tile({ testId, icon: Icon, title, value, sub, to }) {
  const inner = (
    <div data-testid={testId}
         className="rounded-2xl border border-slate-200 bg-white p-4 h-full hover:border-emerald-300 hover:shadow-sm transition">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-[0.18em] font-semibold">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="mt-1 font-display text-3xl font-bold">{value}</div>
      <div className="text-xs text-slate-600 mt-1">{sub}</div>
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}
